import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as authApi from '../api/auth';
import { useAuth } from '../context/AuthProvider';

export const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<{ email: string; role: string; patient_first_name: string; patient_last_name: string } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    authApi.getInvitation(token)
      .then((data) => setInvite(data))
      .catch((err) => setError(err?.response?.data?.detail || 'Invalid invitation'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    try {
      await authApi.acceptInvite(token);
      navigate('/patient');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading invitation...</div>;
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow text-center">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Invitation Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/login" className="text-blue-600 hover:underline">Back to login</Link>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow text-center">
          <h2 className="text-2xl font-bold mb-4">You're Invited!</h2>
          {invite && (
            <p className="text-gray-600 mb-4">
              You've been invited as a <strong>{invite.role}</strong>. Log in or sign up to accept.
            </p>
          )}
          <div className="space-y-2">
            <Link to={`/login?invite=${token}`} className="block w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">Log In</Link>
            <Link to={`/signup?invite=${token}`} className="block w-full bg-white text-blue-600 py-2 rounded border border-blue-600 hover:bg-blue-50">Sign Up</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md p-8 bg-white rounded-lg shadow text-center">
        <h2 className="text-2xl font-bold mb-4">Accept Invitation</h2>
        {invite && (
          <p className="text-gray-600 mb-4">
            You've been invited as a <strong>{invite.role}</strong> for this patient.
          </p>
        )}
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        <button onClick={handleAccept} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
          Accept Invitation
        </button>
      </div>
    </div>
  );
};